import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import clsx from 'clsx';

import styles from './AccordionCard.module.scss';

/*
Temporary Accordion component, to be added to postmaster-react library once stickersheet is updated.
*/

export type AccordionCardProps = {
  heading: JSX.Element;
  id: string;
  icon?: React.ReactNode;
  isInitiallyCollapsed?: boolean;
  onToggle?: (isOpen?: boolean) => void;
} & React.HTMLAttributes<HTMLDivElement>;

const KEYS = {
  Enter: 'Enter',
  Space: ' ',
  Backspace: 'Backspace',
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
  Number: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
  Tab: 'Tab',
};

export const AccordionCard: React.FC<AccordionCardProps> = ({
  heading,
  children,
  icon,
  id,
  isInitiallyCollapsed = true,
  onToggle,
  className,
  ...props
}) => {
  const [isOpen, setOpen] = React.useState(!isInitiallyCollapsed);
  const togglePanel = (): void => {
    if (onToggle) onToggle(isOpen);
    setOpen(!isOpen);
  };

  const onKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === KEYS.Space || event.key === KEYS.Enter) {
      event.preventDefault();
      togglePanel();
    }
  };

  const expandCollapseClassSuffix = isOpen ? 'expanded' : 'collapsed';
  const containerClassNames = clsx(styles[`accordion-card-container-${expandCollapseClassSuffix}`], className);

  return (
    <div className={containerClassNames} {...props}>
      <div
        className={styles['accordion-card-header']}
        id={id}
        aria-expanded={isOpen}
        tabIndex={0}
        role='button'
        onKeyPress={onKeyPress}
        onClick={togglePanel}
      >
        {icon && <span className={styles['accordion-card-custom-icon-container']}>{icon}</span>}
        <span className={styles['accordion-card-title']}>{heading}</span>
        <span className={styles[`accordion-card-chevron-${expandCollapseClassSuffix}`]} />
      </div>
      <AnimateHeight
        role='region'
        aria-labelledby={id}
        animateOpacity={true}
        duration={200}
        height={isOpen ? 'auto' : 0}
      >
        <div className={styles[`accordion-card-contents-body`]}>{children}</div>
      </AnimateHeight>
    </div>
  );
};
